import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { Box, Grid, Slider, Stack, styled } from "@mui/material";
import VSNTypography from "../../../shared/VSNTypography";
import useOTBStyle from "../OTBStyle";
import ViolinBoxPlotChartComponent from "./ViolinBoxPlotChartComponent";
import { BloodGlucose, Hemoglobin, UrineGlucose } from "../../../shared/icon";
import PaiChartComponent from "./PaiChartComponent";
import { capitalizeFirstLetter } from "../../../utils/javascript";

const CardStyled = styled(Stack)(({ theme }) => ({
  padding: 30,
  background: "var(--white)",
  boxShadow: "0px 0px 16px 0px #00000014",
  borderRadius: "16px",
  marginTop: 30,
  [theme.breakpoints.down("xl")]: {
    padding: 20,
  },
  [theme.breakpoints.down("lg")]: {
    padding: 15,
  },
  "& svg": {
    width: 24,
  },
  "& .MuiTypography-h4": {
    fontSize: 20,
    fontWeight: 500,
    color: "#202024",
  },
  "& .MuiTypography-h5": {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: "30px",
    color: "#898994",
    marginBottom: 4,
  },
  "& .MuiTypography-h3": {
    fontWeight: 700,
    lineHeight: 1,
    color: "#000000",
    "& span": {
      fontSize: 18,
      fontWeight: 400,
      lineHeight: "22px",
      color: "var(--gray)",
    },
  },
}));

const MetabolicLabs = ({ data, pdf }) => {
  const classes = useOTBStyle();
  const visceralFatValues = data?.map((patient) => patient.visceral_fat);
  const visceralValues = visceralFatValues.map(Number);
  const visceralMAX = Math.max(...visceralValues) ?? 0;
  const visceralMIN = Math.min(...visceralValues) ?? 0;
  const [visceralFat, setVisceralFat] = useState([visceralMIN, visceralMAX]);

  const BodyFatValues = data?.map((patient) => patient.body_fat_percentage);
  const bodyNumericValues = BodyFatValues.map(Number);
  const bodyFatMAX = Math.max(...bodyNumericValues) ?? 0;
  const bodyFatMIN = Math.min(...bodyNumericValues) ?? 0;
  const [bodyFat, setBodyFat] = useState([bodyFatMIN, bodyFatMAX]);

  const groupBy = (key) =>
    data?.reduce((acc, item) => {
      const value = item[key];
      acc[value] = acc[value] || [];
      acc[value].push(item);
      return acc;
    }, {});

  useEffect(() => {
    setVisceralFat([visceralMIN, visceralMAX]);
  }, [visceralMIN, visceralMAX]);

  useEffect(() => {
    setBodyFat([bodyFatMIN, bodyFatMAX]);
  }, [bodyFatMIN, bodyFatMAX]);

  const handleChange1 = (event, newValue) => {
    setVisceralFat(newValue);
  };

  const handleChange2 = (event, newValue) => {
    setBodyFat(newValue);
  };

  const filterByVisceralFat = data.filter(
    (patient) =>
      patient.visceral_fat >= visceralFat[0] &&
      patient.visceral_fat <= visceralFat[1]
  );

  const filterByBodyFat = filterByVisceralFat.filter(
    (patient) =>
      patient.body_fat_percentage >= bodyFat[0] &&
      patient.body_fat_percentage <= bodyFat[1]
  );

  const DiabiticData = Object.entries(groupBy("diabetes") || {})
    .filter(([key]) => key && key.toLowerCase() !== "null")
    .map(([key, items]) => ({
      name: capitalizeFirstLetter(key),
      value: items.length,
    }));

  const hemoglobinData = filterByBodyFat.filter(
    (patient) => patient.hemoglobin_a1c
  );

  const hemoglobinValues =
    hemoglobinData?.map((patient) => patient?.hemoglobin_a1c) ?? [];

  const numericValues = hemoglobinValues
    ?.map(Number)
    ?.filter((value) => !isNaN(value) && value !== null && value !== undefined);

  const minHemoglobin =
    numericValues?.length > 0 ? Math.min(...numericValues) : 0;
  const maxHemoglobin =
    numericValues?.length > 0 ? Math.max(...numericValues) : 0;
  const avgHemoglobin =
    numericValues?.length > 0
      ? numericValues?.reduce((sum, value) => sum + value, 0) /
        numericValues?.length
      : 0;

  const normalizedValue =
    maxHemoglobin !== minHemoglobin
      ? ((avgHemoglobin - minHemoglobin) / (maxHemoglobin - minHemoglobin)) *
        100
      : 0;

  const HemoglobinData = filterByBodyFat?.map((item) => item?.hemoglobin_a1c);
  const HemoglobinRanges = [
    { label: "Healthy", min: 4, max: 5.5, color: "#7CAB53" },
    { label: "Pre-Diabetes", min: 5.6, max: 6.5, color: "#C9D9B5" },
    { label: "Diabetes", min: 6.6, max: 7, color: "#E8D0B1" },
    { label: "Uncontrolled Diabetes", min: 7, max: 15, color: "#F5B1AC" },
  ];

  return (
    <Box>
      {pdf && (
        <Box
          className={classes.pdfReportLayout}
          sx={{ pageBreakAfter: "always", display: pdf ? "block" : "none" }}
        >
          <VSNTypography variant="h4">Metabolic Labs</VSNTypography>
          <Stack gap={2}>
            <Stack direction="row">
              <ViolinBoxPlotChartComponent
                data={data}
                title="Hemoglobin A1c"
                yData={HemoglobinData}
                ranges={HemoglobinRanges}
                horizontal
                width="600px"
                height="360px"
              />

              {DiabiticData?.length > 0 && (
                <PaiChartComponent
                  data={DiabiticData}
                  height="400px"
                  width="460px"
                  color={[
                    "#7CAB53",
                    "#E0BE94",
                    "#E8D0B1",
                    "#F1E1CD",
                    "#B3B3B3",
                  ]}
                  pdf
                  title="Count of Hemoglobin A1c"
                  lable
                />
              )}

              {/* <Stack direction="column" justifyContent="end" gap={2}>
              <Stack
                gap={1}
                sx={{
                  padding: "20px",
                  backgroundColor: "#F1E1CD",
                  borderRadius: "12px",
                  width: "300px",
                }}
              >
                <Stack direction="row" spacing={2}>
                  <Box>
                    <VSNTypography
                      variant="h6"
                      sx={{
                        cursor: "pointer",
                        fontWeight: 600,
                        color: "#0A0A0B",
                        lineHeight: "31.5px",
                      }}
                    >
                      Visceral Fat
                    </VSNTypography>
                    <VSNTypography
                      variant="body2"
                      sx={{
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#6E7079",
                        lineHeight: "23.44px",
                      }}
                    >
                      Low ({visceralMIN}) - High ({visceralMAX})
                    </VSNTypography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    ".MuiSlider-track": {
                      backgroundColor: "#B29263",
                    },
                  }}
                >
                  <Slider
                    value={visceralFat}
                    onChange={handleChange1}
                    valueLabelDisplay="auto"
                    min={visceralMIN}
                    max={visceralMAX}
                  />
                </Box>
              </Stack>

              <Stack
                spacing={1}
                sx={{
                  padding: "20px",
                  backgroundColor: "#F1E1CD",
                  borderRadius: "12px",
                  width: "300px",
                }}
              >
                <Stack direction="row" spacing={2}>
                  <Box>
                    <VSNTypography
                      variant="h6"
                      sx={{
                        cursor: "pointer",
                        fontWeight: 600,
                        color: "#0A0A0B",
                        lineHeight: "31.5px",
                      }}
                    >
                      Body Fat %
                    </VSNTypography>
                    <VSNTypography
                      variant="body2"
                      sx={{
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#6E7079",
                        lineHeight: "23.44px",
                      }}
                    >
                      Ideal: Male-15% Female-27%
                    </VSNTypography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    ".MuiSlider-track": {
                      backgroundColor: "#B29263",
                    },
                  }}
                >
                  <Slider
                    value={bodyFat}
                    onChange={handleChange2}
                    valueLabelDisplay="auto"
                    min={bodyFatMIN}
                    max={bodyFatMAX}
                  />
                </Box>
              </Stack>
            </Stack> */}
            </Stack>

            <Stack direction="row" gap={2}>
              <Stack gap={2} direction="row">
                <Box className={classes.pdfReportCard}>
                  <Stack direction="row" spacing={1}>
                    <Hemoglobin />
                    <VSNTypography variant="h2">
                      Min, Average, & Max Hemoglobin A1c
                    </VSNTypography>
                  </Stack>

                  <ReactECharts
                    option={{
                      series: [
                        {
                          type: "gauge",
                          startAngle: 180,
                          endAngle: 0,
                          center: ["50%", "80%"],
                          radius: "140%",
                          pointer: {
                            show: false,
                          },
                          progress: {
                            show: true,
                            overlap: false,
                            roundCap: true,
                            clip: false,
                            itemStyle: {
                              color: "#7CAB53",
                            },
                          },
                          axisLine: {
                            roundCap: true,
                            lineStyle: {
                              color: [[1, "#FAF3EA"]],
                              width: 16,
                            },
                          },
                          splitLine: {
                            show: false,
                          },
                          axisTick: {
                            show: false,
                          },
                          axisLabel: {
                            show: true,
                            distance: 270,
                            fontSize: 18,
                            formatter: (value) => {
                              if (value === 0) {
                                return `${maxHemoglobin.toFixed(2)}`;
                              }
                              if (value === 100) {
                                return `${minHemoglobin.toFixed(2)}`;
                              }
                              return "";
                            },
                          },

                          data: [
                            {
                              value: normalizedValue.toFixed(2),
                              title: {
                                offsetCenter: ["10%", "-30%"],
                              },
                              detail: {
                                formatter: `{value|${avgHemoglobin.toFixed(
                                  2
                                )}}`,
                                rich: {
                                  value: {
                                    fontSize: 22,
                                    fontWeight: "bold",
                                    color: "#333",
                                  },
                                },
                                valueAnimation: false,
                                offsetCenter: ["0%", "0%"],
                              },
                            },
                          ],
                          animation: false,
                          animationDuration: 0,
                          animationDurationUpdate: 0,
                        },
                      ],
                    }}
                    style={{ height: "200px", width: "380px" }}
                  />
                </Box>
                <Stack direction="row" gap={2}>
                  <Box
                    className={classes.pdfReportCard}
                    minWidth="200px"
                    height="min-content"
                  >
                    <Stack direction="row" spacing={1}>
                      <BloodGlucose />
                      <VSNTypography variant="h2">Blood Glucose</VSNTypography>
                    </Stack>
                    <VSNTypography variant="h3">Greater than 99</VSNTypography>
                    <VSNTypography
                      className="record"
                      sx={{ marginTop: "0 !important" }}
                    >
                      {
                        filterByBodyFat.filter(
                          (patient) => patient.blood_glucose === 1
                        ).length
                      }{" "}
                      / {filterByBodyFat.length}
                    </VSNTypography>
                  </Box>
                  <Box
                    className={classes.pdfReportCard}
                    minWidth="200px"
                    height="min-content"
                  >
                    <Stack direction="row" spacing={1}>
                      <UrineGlucose />
                      <VSNTypography variant="h2">Urine Glucose</VSNTypography>
                    </Stack>
                    <VSNTypography variant="h3">Positive</VSNTypography>
                    <VSNTypography
                      className="record"
                      sx={{ marginTop: "0 !important" }}
                    >
                      {
                        filterByBodyFat.filter(
                          (patient) => patient.urine_glucose === 1
                        ).length
                      }{" "}
                      / {filterByBodyFat.length}
                    </VSNTypography>
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      )}

      {!pdf && (
        <Box
          className={classes.reportLayout}
          sx={{ display: pdf ? "none" : "block" }}
        >
          <div className={classes.stickyTitle}>
            <VSNTypography variant="h2">Metabolic Labs</VSNTypography>
          </div>
          <Box>
            <Grid
              container
              columnSpacing={{ xxl: 3, xs: 1 }}
              flexDirection={{ md: "row-reverse" }}
            >
              <Grid item xs={12} lg={5}>
                <Stack
                  direction={{
                    xl: "row",
                    lg: "column",
                    sm: "row",
                    xs: "column",
                  }}
                  alignItems="center"
                  justifyContent="end"
                  spacing={3}
                  sx={{ marginBottom: "32px" }}
                >
                  <Stack
                    spacing={2}
                    sx={{
                      backgroundColor: "#F1E1CD",
                      borderRadius: "12px",
                      width: "100%",
                    }}
                    p={2}
                  >
                    <Stack
                      direction="row"
                      spacing={4}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <VSNTypography
                          variant="h6"
                          sx={{
                            cursor: "pointer",
                            fontWeight: 700,
                            color: "#0A0A0B",
                            lineHeight: "31.5px",
                          }}
                        >
                          Visceral Fat
                        </VSNTypography>
                        <VSNTypography
                          variant="body2"
                          sx={{
                            cursor: "pointer",
                            fontSize: "18px",
                            fontWeight: 500,
                            color: "#6E7079",
                            lineHeight: "23.44px",
                          }}
                        >
                          Low ({visceralMIN}) - High ({visceralMAX})
                        </VSNTypography>
                      </Box>
                      <VSNTypography
                        component="h4"
                        variant="h4"
                        sx={{ fontSize: "40px" }}
                      >
                        {data?.filter(({ vitals }) => vitals === 1)?.length}
                      </VSNTypography>
                    </Stack>
                    <Box>
                      <Slider
                        value={visceralFat}
                        onChange={handleChange1}
                        valueLabelDisplay="auto"
                        color="natural"
                        min={visceralMIN}
                        max={visceralMAX}
                      />
                    </Box>
                  </Stack>
                  <Stack
                    spacing={2}
                    sx={{
                      backgroundColor: "#F1E1CD",
                      borderRadius: "12px",
                      width: "100%",
                    }}
                    p={2}
                  >
                    <Stack
                      direction="row"
                      spacing={4}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <VSNTypography
                          variant="h6"
                          sx={{
                            cursor: "pointer",
                            fontWeight: 700,
                            color: "#0A0A0B",
                            lineHeight: "31.5px",
                          }}
                        >
                          Body Fat %
                        </VSNTypography>
                        <VSNTypography
                          variant="body2"
                          sx={{
                            cursor: "pointer",
                            fontSize: "18px",
                            fontWeight: 500,
                            color: "#6E7079",
                            lineHeight: "23.44px",
                          }}
                        >
                          Ideal: Male-15% Female-27%
                        </VSNTypography>
                      </Box>
                      <VSNTypography
                        component="h4"
                        variant="h4"
                        sx={{ fontSize: "40px" }}
                      >
                        {data?.filter(({ vitals }) => vitals === 1)?.length}
                      </VSNTypography>
                    </Stack>
                    <Box>
                      <Slider
                        value={bodyFat}
                        onChange={handleChange2}
                        valueLabelDisplay="auto"
                        color="natural"
                        min={bodyFatMIN}
                        max={bodyFatMAX}
                      />
                    </Box>
                  </Stack>
                </Stack>
                {/* <Box
              // sx={{
              //   height: { lg: 450, smd: 400, xs: 350 },
              //   display: { xs: "none", xl: "block" },
              // }}
              >
                <PaiChartComponent
                  data={DiabiticData}
                  color={[
                    "#7CAB53",
                    "#E0BE94",
                    "#E8D0B1",
                    "#F1E1CD",
                    "#B3B3B3",
                  ]}
                  height="450px"
                  width="300px"
                  title="Count of Hemoglobin A1c"
                />
              </Box> */}
              </Grid>
              <Grid item xs={12} lg={7}>
                <ViolinBoxPlotChartComponent
                  data={data}
                  title="Hemoglobin A1c"
                  yData={HemoglobinData}
                  ranges={HemoglobinRanges}
                  horizontal
                  height="320px"
                />
              </Grid>
            </Grid>
          </Box>
          <Grid container columnSpacing={4}>
            <Grid item lg={6} xs={12}>
              <CardStyled>
                <Stack direction="row" spacing={1}>
                  <Hemoglobin />
                  <VSNTypography component="h4" variant="h4">
                    Min, Average, & Max Hemoglobin A1c
                  </VSNTypography>
                </Stack>
                <Box sx={{ height: { xxl: 240, sm: 200, xs: 150 } }}>
                  <ReactECharts
                    option={{
                      series: [
                        {
                          type: "gauge",
                          startAngle: 180,
                          endAngle: 0,
                          center: ["50%", "80%"],
                          radius: "140%",
                          pointer: {
                            show: false,
                          },
                          progress: {
                            show: true,
                            overlap: false,
                            roundCap: true,
                            clip: false,
                            itemStyle: {
                              color: "#7CAB53",
                            },
                          },
                          axisLine: {
                            roundCap: true,
                            lineStyle: {
                              color: [[1, "#FAF3EA"]],
                              width: 20,
                            },
                          },
                          splitLine: {
                            show: false,
                          },
                          axisTick: {
                            show: false,
                          },
                          axisLabel: {
                            show: true,
                            distance: 320,
                            fontSize: 18,
                            formatter: (value) => {
                              if (value === 0) {
                                return `${maxHemoglobin.toFixed(2)}`;
                              }
                              if (value === 100) {
                                return `${minHemoglobin.toFixed(2)}`;
                              }
                              return "";
                            },
                          },

                          data: [
                            {
                              value: normalizedValue.toFixed(2),
                              title: {
                                offsetCenter: ["10%", "-30%"],
                              },
                              detail: {
                                formatter: `{value|${avgHemoglobin.toFixed(
                                  2
                                )}}`,
                                rich: {
                                  value: {
                                    fontSize: 22,
                                    fontWeight: "bold",
                                    color: "#333",
                                  },
                                },
                                valueAnimation: false,
                                offsetCenter: ["0%", "0%"],
                              },
                            },
                          ],
                          animation: false,
                          animationDuration: 0,
                          animationDurationUpdate: 0,
                        },
                      ],
                    }}
                    style={{ height: "230px", width: "100%" }}
                  />
                </Box>
              </CardStyled>
            </Grid>

            <Grid item xs={12} lg={6}>
              {DiabiticData?.length > 0 && (
                <Box sx={{ height: { smd: 300, xs: 350 }, marginTop: 3 }}>
                  <PaiChartComponent
                    data={DiabiticData}
                    color={[
                      "#7CAB53",
                      "#E0BE94",
                      "#E8D0B1",
                      "#F1E1CD",
                      "#B3B3B3",
                    ]}
                    height="300px"
                    width="550px"
                  />
                </Box>
              )}
            </Grid>
            <Grid item lg={3} xsm={6} xs={12}>
              <CardStyled>
                <Stack direction="row" spacing={1} alignItems="center">
                  <BloodGlucose />
                  <VSNTypography component="h4" variant="h4">
                    Blood Glucose
                  </VSNTypography>
                </Stack>
                <VSNTypography component="h5" variant="h5">
                  Greater than 99
                </VSNTypography>

                <VSNTypography component="h3" variant="h3">
                  {
                    filterByBodyFat.filter(
                      (patient) => patient.blood_glucose === 1
                    ).length
                  }{" "}
                  / {filterByBodyFat.length}
                  <span
                    style={{
                      color:
                        filterByBodyFat.filter(
                          (patient) => patient.blood_glucose === null
                        ).length > 0
                          ? "red"
                          : "green",
                    }}
                  >
                    {" "}
                    (
                    {filterByBodyFat.filter(
                      (patient) => patient.blood_glucose === null
                    ).length > 0
                      ? `-${
                          filterByBodyFat.filter(
                            (patient) => patient.blood_glucose === null
                          ).length
                        }`
                      : 0}
                    )
                  </span>
                </VSNTypography>
              </CardStyled>
            </Grid>
            <Grid item lg={3} xsm={6} xs={12}>
              <CardStyled>
                <Stack direction="row" spacing={1} alignItems="center">
                  <UrineGlucose />
                  <VSNTypography component="h4" variant="h4">
                    Urine Glucose
                  </VSNTypography>
                </Stack>
                <VSNTypography component="h5" variant="h5">
                  Positive
                </VSNTypography>
                {/* <VSNTypography component="h3" variant="h3">
                {
                  filterByBodyFat.filter(
                    (patient) => patient.urine_glucose === 1
                  ).length
                }{" "}
                / {filterByBodyFat.length}
              </VSNTypography> */}

                <VSNTypography component="h3" variant="h3">
                  {
                    filterByBodyFat.filter(
                      (patient) => patient.urine_glucose === 1
                    ).length
                  }{" "}
                  / {filterByBodyFat.length}
                  <span
                    style={{
                      color:
                        filterByBodyFat.filter(
                          (patient) => patient.urine_glucose === null
                        ).length > 0
                          ? "red"
                          : "green",
                    }}
                  >
                    {" "}
                    (
                    {filterByBodyFat.filter(
                      (patient) => patient.urine_glucose === null
                    ).length > 0
                      ? `-${
                          filterByBodyFat.filter(
                            (patient) => patient.urine_glucose === null
                          ).length
                        }`
                      : 0}
                    )
                  </span>
                </VSNTypography>
              </CardStyled>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default MetabolicLabs;
